import { getClientCacheLoaderData } from '@/hooks/useClientCacheLoaderData';
import { isServer } from '@/shared/util/ssr';
import { getTemplateKeysFromLocal } from '@/ui-kit/components/TemplatingEditor/utils/save-template-str-to-local';
import type { IEnvironment } from '@tarslib/types/lib/environment';
import { LocalStorageKey } from '../LocalStorageKey';
import getCompatibleLocalStorage from './getCompatibleLocalStorage';
import { StorageType } from '@/shared/constant/StorageType';

const BUILTIN_ENVIRONMENT: IEnvironment = {
  id: 0,
  name: 'BUILTIN_ENVIRONMENT',
  baseUrl: '',
  baseUrls: {},
  parameters: {
    query: [],
    body: [],
    cookie: [],
    header: [],
  },
  variables: [],
  visibility: 'private' as IEnvironment['visibility'],
  type: 'normal' as IEnvironment['type'],
  projectId: 0,
  creatorId: 0,
};

const loadEnvironmentBuiltinMap: (storageType: StorageType) => Record<string, IEnvironment> = (storageType) => {
  try {
    const value = getCompatibleLocalStorage(storageType).getItem(LocalStorageKey.USE_ENVIRONMENT_BUILTIN)
    if (!value) {
      return {};
    }
    return JSON.parse(value);
  } catch (error) {
    console.error(error);
    return {};
  }
};

const saveEnvironmentBuiltin = (docsId: string | number, value: IEnvironment, storageType: StorageType) => {
  try {
    const environmentBuiltinMap = loadEnvironmentBuiltinMap(storageType);
    environmentBuiltinMap[docsId] = value;
    getCompatibleLocalStorage(storageType).setItem(
      LocalStorageKey.USE_ENVIRONMENT_BUILTIN,
      JSON.stringify(environmentBuiltinMap),
    );
  } catch (error) {
    console.error(error);
  }
};

const getEnvironmentBuiltinByDocsId: (docsId: string | number, storageType: StorageType) => IEnvironment = (docsId, storageType) => {
  try {
    const environment = loadEnvironmentBuiltinMap(storageType)[docsId];
    if (!environment) {
      return BUILTIN_ENVIRONMENT;
    }
    return environment;
  } catch (error) {
    console.error('Failed to load environment builtin', error);
    return BUILTIN_ENVIRONMENT;
  }
};

export const loadEnvironmentBuiltinByDocsId: (docsId: string | number, storageType: StorageType) => IEnvironment = (
  docsId,
  storageType,
) => {
  const environmentBuiltin = getEnvironmentBuiltinByDocsId(docsId, storageType);
  const templateKeys = !isServer ? getTemplateKeysFromLocal() : [];
  const variablesFromLocal: IEnvironment['variables'] = templateKeys.map((item) => ({
    name: item,
    value: '',
  }));

  // Create a map of existing environment variables for quick lookup
  const existingVariablesMap = new Map(
    environmentBuiltin.variables.map((variable) => [variable.name, variable]),
  );

  // Add new variables that don't exist in environmentBuiltin
  variablesFromLocal.forEach((variable) => {
    if (!existingVariablesMap.has(variable.name)) {
      environmentBuiltin.variables.push(variable);
    }
  });

  return environmentBuiltin;
};

export const saveEnvironmentBuiltinVariables = (
  docsId: string | number,
  variables: IEnvironment['variables'],
  storageType: StorageType
) => {
  const environmentBuiltin = getEnvironmentBuiltinByDocsId(docsId, storageType);
  environmentBuiltin.variables = variables;
  saveEnvironmentBuiltin(docsId, environmentBuiltin, storageType);
};

export const removeEnvironmentBuiltinByDocsId = (docsId: string | number) => {
  const env = getCompatibleLocalStorage(StorageType.LocalStorage).getItem(LocalStorageKey.USE_ENVIRONMENT_BUILTIN);
  if (!env) return
  try {
    const envMap = JSON.parse(env);
    delete envMap[docsId];
    getCompatibleLocalStorage(StorageType.LocalStorage).setItem(LocalStorageKey.USE_ENVIRONMENT_BUILTIN, JSON.stringify(envMap));
  } catch (error) {
    console.error(error);
  }
};
